import { TokenService } from '@/services/auth/tokenService';

export interface ApiClient {
  get<T>(endpoint: string): Promise<T>;
  post<T>(endpoint: string, data?: unknown): Promise<T>;
  put<T>(endpoint: string, data?: unknown): Promise<T>;
  delete<T>(endpoint: string): Promise<T>;
}

export type TokenType = 'id' | 'access';

const apiUrl = process.env.PUBLIC_API_URL || 'http://localhost:8080';

class HttpClient implements ApiClient {
  private baseUrl: string;
  private tokenService: TokenService;
  private tokenProvider?: () => string | null;
  private tokenType: TokenType;

  constructor(
    baseUrl: string,
    tokenProvider?: () => string | null,
    tokenType: TokenType = 'id',
  ) {
    this.baseUrl = baseUrl;
    this.tokenService = TokenService.getInstance();
    this.tokenProvider = tokenProvider;
    this.tokenType = tokenType;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false,
  ): Promise<T> {
    // Get current token, refreshing if necessary
    const token = this.tokenProvider
      ? this.tokenProvider()
      : this.tokenType === 'access'
        ? await this.tokenService.getCurrentAccessToken()
        : await this.tokenService.getCurrentToken();
    const url = `${this.baseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 unauthorized - attempt token refresh once
    if (response.status === 401 && !isRetry) {
      try {
        const refreshedTokenInfo = await this.tokenService.refreshTokens();
        if (refreshedTokenInfo) {
          // Retry the request with the new token
          return this.request<T>(endpoint, options, true);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, fall through to throw the original 401 error
      }
    }

    if (response.status === 401 && isRetry) {
      // If we already retried and still get 401, refresh the token
      throw new Error('Unauthorized: Invalid or expired token after refresh');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const createApiClient = (
  tokenProvider?: () => string | null,
  overrideBaseURL: string = apiUrl,
  tokenType: TokenType = 'id',
): ApiClient => {
  return new HttpClient(overrideBaseURL, tokenProvider, tokenType);
};
